import React, { useMemo, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import combineActions from '@app/utils/combineActions';
import connect from '@app/utils/connect';
import sendNotification from '@app/actions/notifications/send';
import { Notification } from '@app/components/notifications/Notifications';
import DetailsModal from '@app/broadcasts/components/exposures/DetailsModal';
import RouteNames from '@app/RouteNames';
import {
    BroadcastFrameBox,
    BroadcastFrameExample,
    Client,
} from '@app/types/api';
import {
    findBroadcastRspByRouteParams,
    getModifiedBroadcast,
    parseFilterPlacementId,
    resolveFilterPlacementId,
} from '@app/broadcasts/utils/broadcastsUtils';
import { useMediaValueReport } from '@app/components/hooks/useMediaValueReport';
import usePendo from '@app/components/hooks/usePendo';
import useBroadcastsExamples from '@app/broadcasts/hooks/useBroadcastsExamples';
import BroadcastExposurePDF from '@app/broadcasts/pdfs/BroadcastExposurePDF';
import useGeneratePDF from '@app/broadcasts/hooks/useGeneratePDF';
import useDownloadBroadcastReport from '@app/broadcasts/hooks/useDownloadBroadcastReport';
import { DEFAULT_LANGUAGE } from '@app/i18n/constants';
import { useRightsholderAttribution } from '@app/components/hooks/useRightsholderAttribution';
import { rhAttributionPostQueryFields } from '@app/reports/rhAttributionPostQueryFields';
import { EDGE_RIGHTSHOLDER_ATTRIBUTION } from '@app/constants/config';
import { LoadingDots } from '@app/ui';
import { downloadImages } from '@app/broadcasts/utils/frameDownloadUtils';

const pdfTestDomNode = document.getElementById('broadcast-details-modal');

export const getPreferredMetric = (
    primary?: number | null,
    fallback?: number | null,
) => {
    if (typeof primary === 'number' && Number.isFinite(primary)) {
        return primary;
    }

    if (typeof fallback === 'number' && Number.isFinite(fallback)) {
        return fallback;
    }

    return undefined;
};

/**
 * Puts the frame the user is currently viewing first, keeping the remaining
 * frames in their existing order. The PDF template treats index 0 as the
 * primary frame.
 */
export const orderFramesForDownload = <T,>(
    frames: T[],
    frameIndex: number,
): T[] =>
    frameIndex > 0 && frameIndex < frames.length
        ? [
              frames[frameIndex],
              ...frames.filter((_, index) => index !== frameIndex),
          ]
        : frames;

/** A frame as shown in the carousel and exported to the PDF. */
export interface BroadcastFrameFigure {
    frameUrl: string;
    boxes: BroadcastFrameBox[];
    capturedAt: number | null;
    broadcastDuration: number | null;
    frameWidth: number | null;
    frameHeight: number | null;
}

const sameBroadcastFrame = (
    firstUrl: string | null | undefined,
    secondUrl: string,
) => {
    if (firstUrl === secondUrl) return true;
    if (!firstUrl) return false;

    const framePath = (url: string) => {
        try {
            return new URL(url).pathname.match(
                /\/(\d+)\/frames\/([^/]+)$/,
            )?.[0];
        } catch {
            return null;
        }
    };
    const firstPath = framePath(firstUrl);
    return firstPath != null && firstPath === framePath(secondUrl);
};

export const buildBroadcastFrameFigures = (
    frameUrl: string | null | undefined,
    frameExamples: BroadcastFrameExample[],
): BroadcastFrameFigure[] => {
    const matchingExample = frameExamples.find(example =>
        sameBroadcastFrame(frameUrl, example.frameUrl),
    );
    const exampleFrames = frameExamples
        .filter(example => !sameBroadcastFrame(frameUrl, example.frameUrl))
        .map(example => ({
            frameUrl: example.frameUrl,
            boxes: example.boxes ?? [],
            capturedAt: example.capturedAt,
            broadcastDuration: example.broadcastDuration,
            frameWidth: example.frameWidth,
            frameHeight: example.frameHeight,
        }));

    const rspFrame: BroadcastFrameFigure[] = frameUrl
        ? [
              {
                  frameUrl,
                  boxes: matchingExample?.boxes ?? [],
                  capturedAt: matchingExample?.capturedAt ?? null,
                  broadcastDuration:
                      matchingExample?.broadcastDuration ??
                      frameExamples[0]?.broadcastDuration ??
                      null,
                  frameWidth:
                      matchingExample?.frameWidth ??
                      frameExamples[0]?.frameWidth ??
                      null,
                  frameHeight:
                      matchingExample?.frameHeight ??
                      frameExamples[0]?.frameHeight ??
                      null,
              },
          ]
        : [];

    return [...rspFrame, ...exampleFrames].slice(0, 3);
};

export const getExposureFilename = (
    rightsholderName = '',
    contentDate?: string | Date,
) => {
    const dateStr = new Date(contentDate ?? '')
        .toLocaleDateString('en-US')
        .replace(/\//g, '-');

    return `Broadcast_${rightsholderName}_${dateStr}`.replace(/\s/g, '');
};

interface ExposureDetailsProps {
    broadcasts: any;
    filters: any;
    client: Client;
    sendNotification: (
        message: string,
        config?: Notification['options'],
    ) => void;
}

export const ExposureDetails = ({
    broadcasts,
    filters,
    client,
    sendNotification,
}: ExposureDetailsProps) => {
    const { pendo } = usePendo();
    const { t, i18n } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const {
        broadcastName: id,
        rightsholderId,
        sponsorId,
        placementId,
        pdf,
    } = useParams();

    const broadcast = getModifiedBroadcast(filters, broadcasts, id);
    const currentRsp = findBroadcastRspByRouteParams(broadcast?.broadcastRsps, {
        rightsholderId,
        sponsorId,
        placementId,
    });
    const broadcastName = broadcast?.broadcastName;
    const contentDate = broadcast?.contentDate;
    const {
        rightsholder,
        sponsor,
        placement,
        frameUrl,
        networks = [],
        totalVerbalMentions,
        verbalMentionsExamples,
    } = currentRsp ?? {};
    const hasVerbalMentionExposure = (totalVerbalMentions ?? 0) > 0;
    const hasExposureData =
        !!broadcast &&
        !!currentRsp &&
        !!rightsholder &&
        !!sponsor &&
        !!placement;

    const { rightsholderAttribution } = useRightsholderAttribution({ client });
    const useLeagueAttribution =
        client.permissions?.includes(EDGE_RIGHTSHOLDER_ATTRIBUTION) &&
        rightsholderAttribution === 'league';

    const expectedAttributionMode = useLeagueAttribution ? 'league' : 'team';
    const isFiltersReady =
        !filters?.isLoading &&
        (filters?.rightsholderAttributionMode == null ||
            filters?.rightsholderAttributionMode === expectedAttributionMode);

    const filterPlacementId = useMemo(() => {
        if (!currentRsp) return null;

        return resolveFilterPlacementId(
            currentRsp,
            filters?.placements?.list ?? {},
            filters?.rightsholders?.list ?? {},
        );
    }, [currentRsp, filters?.placements?.list, filters?.rightsholders?.list]);

    const { rightsholderId: filterRhId, placementId: filterPlcId } = useMemo(
        () => parseFilterPlacementId(filterPlacementId),
        [filterPlacementId],
    );

    const { isDownloadingReport, generatePDFUtils } = useGeneratePDF();
    const { isDownloadingBroadcastReport, downloadBroadcastReport } =
        useDownloadBroadcastReport();

    const reportQuery = useMemo(() => {
        if (
            !broadcastName ||
            !sponsor ||
            !filterPlacementId ||
            filterRhId == null ||
            filterPlcId == null
        ) {
            return null;
        }

        return {
            mediaUploadNames: [broadcastName],
            rightsholderIds: [filterRhId],
            sponsorIds: [sponsor.id],
            rightsholderPlacementIds: [filterPlacementId],
            groupBy: ['mediaUploadName'],
            displayColumns: [
                'sponsorMediaValue',
                'impressions',
                'exposures',
                'duration',
                'exposuresImpressions',
            ],
            client: { id: client.id },
            ...rhAttributionPostQueryFields(client, rightsholderAttribution),
        };
    }, [
        broadcastName,
        sponsor,
        filterPlacementId,
        filterRhId,
        filterPlcId,
        client,
        rightsholderAttribution,
    ]);

    const { data, isLoading: isLoadingReport } = useMediaValueReport(
        reportQuery,
        { enabled: hasExposureData && isFiltersReady },
    );
    const { data: frameExamples = [] } = useBroadcastsExamples({
        mediaUploadName: broadcastName ?? '',
        contentDate: contentDate ?? '',
        rightsholderId: filterRhId ?? 0,
        sponsorId: sponsor?.id ?? 0,
        placementId: filterPlcId ?? 0,
        clientId: client.id,
        useRightsholderAttribution: useLeagueAttribution,
        enabled:
            hasExposureData &&
            isFiltersReady &&
            !hasVerbalMentionExposure &&
            filterRhId != null &&
            filterPlcId != null,
    });

    const clipBackedVerbalMentionsExamples = useMemo(
        () =>
            (verbalMentionsExamples ?? []).filter(
                vm => typeof vm.videoUrl === 'string' && vm.videoUrl.length > 0,
            ),
        [verbalMentionsExamples],
    );

    const frames: BroadcastFrameFigure[] = useMemo(
        () =>
            hasVerbalMentionExposure
                ? []
                : buildBroadcastFrameFigures(frameUrl, frameExamples),
        [frameExamples, frameUrl, hasVerbalMentionExposure],
    );

    const frameUrls = useMemo(
        () => frames.map(frame => frame.frameUrl),
        [frames],
    );

    const reportTotal =
        (
            data as {
                total: {
                    exposures?: number;
                    exposuresImpressions?: number;
                    duration?: number;
                    sponsorMediaValue?: number;
                };
            }
        )?.total || {};

    const total = useMemo(
        () => ({
            exposures: getPreferredMetric(
                reportTotal.exposures,
                currentRsp?.exposures,
            ),
            exposuresImpressions: getPreferredMetric(
                reportTotal.exposuresImpressions,
                currentRsp?.impressions,
            ),
            duration: getPreferredMetric(
                reportTotal.duration,
                currentRsp?.duration,
            ),
            sponsorMediaValue: getPreferredMetric(
                reportTotal.sponsorMediaValue,
                currentRsp?.sponsorMediaValue,
            ),
        }),
        [currentRsp, reportTotal],
    );

    const vmSample = hasVerbalMentionExposure
        ? {
              mention: verbalMentionsExamples?.at(0)?.mention,
              context:
                  verbalMentionsExamples
                      ?.map(vm => vm.context)
                      .filter((context): context is string => !!context) ?? [],
              videoUrls: clipBackedVerbalMentionsExamples.map(
                  vm => vm.videoUrl as string,
              ),
              hasAvailableClips: clipBackedVerbalMentionsExamples.length > 0,
          }
        : null;

    const trackObject = useMemo(() => {
        if (!total.sponsorMediaValue) return null;

        return {
            broadcast_name: broadcastName,
            rightsholder: rightsholder.name,
            sponsor: sponsor.name,
            placement: placement.name,
            SMV: total.sponsorMediaValue,
            exposure_type: hasVerbalMentionExposure
                ? 'verbal_mention'
                : 'visual',
        };
    }, [
        broadcastName,
        rightsholder,
        sponsor,
        placement,
        total,
        hasVerbalMentionExposure,
    ]);

    useEffect(() => {
        if (trackObject && pendo) {
            pendo.track(
                'Relo Edge::Broadcast Exposure::Exposures::Sample Detail::View',
                trackObject,
            );
        }
    }, [trackObject, pendo]);

    const getReport = async (frameIndex = 0) => {
        pendo.track(
            'Relo Edge::Broadcast Exposure::Exposures::Sample Detail::Download',
            trackObject,
        );

        const filename = getExposureFilename(rightsholder.name, contentDate);

        if (!hasVerbalMentionExposure) {
            const orderedFrames = orderFramesForDownload(frames, frameIndex);
            const orderedFrameUrls = orderedFrames.map(frame => frame.frameUrl);

            await downloadBroadcastReport({
                reqBody: {
                    contentDate,
                    placement,
                    mediaUploadName: broadcastName,
                    frameUrl: orderedFrameUrls[0],
                    frameUrls: orderedFrameUrls,
                    frames: orderedFrames,
                    broadcastNetworkData: {
                        networks: networks.filter(Boolean),
                        exposures: total.exposures,
                        exposuresImpressions: total.exposuresImpressions,
                        duration: total.duration,
                        sponsorMediaValue: total.sponsorMediaValue,
                    },
                    secondaryColor: client.secondaryColor || '#012d62',
                    rightsholder,
                    sponsor,
                    locale: i18n.language || DEFAULT_LANGUAGE,
                    currency: client.currency.code,
                    reportTitle: filename,
                    clientId: client.id,
                    primaryColor: client.primaryColor || '#009FDF',
                    ...rhAttributionPostQueryFields(
                        client,
                        rightsholderAttribution,
                    ),
                },
                filename,
                sendNotification,
            });
            return;
        }

        generatePDFUtils({
            component: (
                <BroadcastExposurePDF
                    broadcastName={broadcastName}
                    exposureType={
                        hasVerbalMentionExposure ? 'verbal_mention' : 'visual'
                    }
                    frameUrls={[]}
                    verbalMentionsExample={
                        vmSample
                            ? {
                                  ...vmSample,
                                  videoUrl: vmSample.hasAvailableClips
                                      ? window.location.href
                                      : undefined,
                              }
                            : null
                    }
                    rightsholder={rightsholder}
                    sponsor={sponsor}
                    placement={placement}
                    total={total}
                    currencyCode={client.currency.code}
                    networks={networks}
                />
            ),
            filename,
            sendNotifications: sendNotification,
            language: i18n.language || DEFAULT_LANGUAGE,
        });
    };

    const downloadFrameImage = async (frameIndex = 0) => {
        pendo.track(
            'Relo Edge::Broadcast Exposure::Exposures::Sample Detail::Download JPEG',
            trackObject,
        );

        const failed = await downloadImages([
            {
                url: frameUrls[frameIndex],
                filename: `${getExposureFilename(rightsholder.name, contentDate)}_frame${frameIndex + 1}.jpg`,
            },
        ]);
        if (failed) {
            sendNotification(
                t('measurement:broadcastExposure.downloadFramesFailed'),
                {
                    context: 'error',
                },
            );
        }
    };

    const onClose = () => {
        const pathSegments = location.pathname.split('/').filter(Boolean);
        const modalSegmentsLength =
            RouteNames.broadcastModal.path.split('/').length;
        const backPath =
            '/' +
            pathSegments
                .slice(
                    0,
                    -(pdf ? modalSegmentsLength : modalSegmentsLength - 1),
                )
                .join('/');

        navigate(backPath, { replace: true });
    };

    if (!hasExposureData) {
        return <LoadingDots size="lg" className="-p-t-6 -m-t-6" />;
    }

    if (pdf) {
        return ReactDOM.createPortal(
            <div
                className="modal-overlay modal-shown"
                style={{ zIndex: 1200, overflow: 'scroll' }}
                onClick={onClose}
            >
                <div
                    className="modal-v2-wrapper"
                    style={{
                        maxWidth: '90%',
                    }}
                    onClick={e => e.stopPropagation()}
                >
                    <BroadcastExposurePDF
                        broadcastName={broadcastName}
                        exposureType={
                            hasVerbalMentionExposure
                                ? 'verbal_mention'
                                : 'visual'
                        }
                        frameUrls={hasVerbalMentionExposure ? [] : frameUrls}
                        verbalMentionsExample={
                            vmSample
                                ? {
                                      ...vmSample,
                                      videoUrl: vmSample.hasAvailableClips
                                          ? window.location.href
                                          : undefined,
                                  }
                                : null
                        }
                        rightsholder={rightsholder}
                        sponsor={sponsor}
                        placement={placement}
                        total={total}
                        currencyCode={client.currency.code}
                        networks={networks}
                    />
                </div>
            </div>,
            pdfTestDomNode,
        );
    }

    return (
        <DetailsModal
            broadcastName={broadcastName}
            frameUrls={frameUrls}
            verbalMentionsExample={vmSample}
            rightsholder={rightsholder}
            sponsor={sponsor}
            placement={placement}
            disableDownload={
                hasVerbalMentionExposure
                    ? !vmSample?.hasAvailableClips
                    : frameUrls.length === 0
            }
            isDownloadingReport={isDownloadingReport}
            isLoadingReport={isLoadingReport || isDownloadingBroadcastReport}
            total={total}
            currencyCode={client.currency.code}
            networks={networks}
            onTrackVideoPlayed={() => {
                if (trackObject && pendo) {
                    pendo.track(
                        'Relo Edge::Broadcast Exposure::Exposures::Sample Detail::Video Played',
                        trackObject,
                    );
                }
            }}
            getReport={getReport}
            downloadFrameImage={downloadFrameImage}
            onClose={onClose}
        />
    );
};

export default connect(
    ['broadcasts', 'filters', 'client'],
    combineActions({ sendNotification }),
)(ExposureDetails);
